import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Smartphone,
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Mail,
  User,
  DollarSign,
  Calendar,
  Filter,
  Download,
  Eye,
  Check,
  X,
  MessageCircle,
  Bell,
  Send
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const MobilePaymentsManager = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [pendingReminders, setPendingReminders] = useState(0);
  const [sendingReminders, setSendingReminders] = useState(false);

  const token = localStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const limit = 10;

  useEffect(() => {
    loadRequests();
    loadPendingRemindersCount();
  }, [page, statusFilter, methodFilter]);

  const loadPendingRemindersCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/mobile-payments/pending-count`, {
        headers,
        withCredentials: true
      });
      setPendingReminders(response.data.pending_needing_reminder || 0);
    } catch (error) {
      console.error('Error loading pending count:', error);
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/mobile-payments/send-reminders`, {}, {
        headers,
        withCredentials: true
      });
      
      toast.success(`${response.data.reminders_sent} rappel(s) envoyé(s) !`);
      setPendingReminders(0);
      loadRequests();
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Erreur lors de l\'envoi des rappels');
    } finally {
      setSendingReminders(false);
    }
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (methodFilter !== 'all') params.append('method', methodFilter);
      if (search) params.append('search', search);

      const response = await axios.get(`${API}/admin/mobile-payments?${params}`, {
        headers,
        withCredentials: true
      });
      
      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading mobile payments:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadRequests();
  };

  const updateStatus = async (requestId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/mobile-payments/${requestId}/status`, 
        { status: newStatus },
        { headers, withCredentials: true }
      );
      
      toast.success(`Statut mis à jour: ${getStatusLabel(newStatus)}`);
      loadRequests();
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Paiement confirmé',
      processed: 'Traité (billets émis)',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'processed': return <Check className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getMethodColor = (method) => {
    return method === 'moncash' 
      ? 'bg-yellow-500 text-white' 
      : 'bg-blue-600 text-white';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    const csvContent = [
      ['ID', 'Date', 'Type', 'Méthode', 'Montant', 'Email', 'Nom', 'Statut'].join(','),
      ...requests.map(r => [
        r.request_id,
        formatDate(r.created_at),
        r.payment_type === 'purchase' ? 'Achat' : 'Don',
        r.payment_method.toUpperCase(),
        `$${r.amount}`,
        r.email,
        r.name || '-',
        getStatusLabel(r.status)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `mobile_payments_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const totalPages = Math.ceil(total / limit);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="mobile-payments-manager">
      {/* Header Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">En attente</p>
              <p className="text-2xl font-bold text-yellow-600">
                {requests.filter(r => r.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Confirmés</p>
              <p className="text-2xl font-bold text-blue-600">
                {requests.filter(r => r.status === 'confirmed').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Traités</p>
              <p className="text-2xl font-bold text-green-600">
                {requests.filter(r => r.status === 'processed').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total demandes</p>
              <p className="text-2xl font-bold text-gray-700">{total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Reminder Alert */}
      {pendingReminders > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <Bell className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-800">
                {pendingReminders} demande(s) en attente depuis +48h
              </p>
              <p className="text-sm text-amber-600">
                Ces utilisateurs n'ont pas encore complété leur paiement
              </p>
            </div>
          </div>
          <Button
            onClick={sendReminders}
            disabled={sendingReminders}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {sendingReminders ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Envoyer les rappels
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="status-filter"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="confirmed">Confirmé</option>
              <option value="processed">Traité</option>
              <option value="cancelled">Annulé</option>
            </select>
            
            {/* Method Filter */}
            <select
              value={methodFilter}
              onChange={(e) => { setMethodFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="method-filter"
            >
              <option value="all">Toutes les méthodes</option>
              <option value="moncash">Moncash</option>
              <option value="natcash">Natcash</option>
            </select>
          </div>
          
          <div className="flex gap-2">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-48"
              />
              <Button type="submit" variant="outline" size="sm">
                <Search className="w-4 h-4" />
              </Button>
            </form>
            
            {/* Refresh */}
            <Button onClick={loadRequests} variant="outline" size="sm">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            {/* Export */}
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Méthode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Chargement...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Aucune demande de paiement mobile
                  </td>
                </tr>
              ) : (
                requests.map((request) => (
                  <tr key={request.request_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getMethodColor(request.payment_method)}`}>
                        {request.payment_method.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {request.payment_type === 'purchase' ? (
                        <span className="text-blue-600 font-medium">
                          Achat ({request.quantity} billet{request.quantity > 1 ? 's' : ''})
                        </span>
                      ) : (
                        <span className="text-pink-600 font-medium">Don</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">
                      ${request.amount}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{request.name || '-'}</p>
                        <p className="text-gray-500">{request.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        {getStatusLabel(request.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedRequest(request)}
                          className="h-8 w-8 p-0"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {request.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateStatus(request.request_id, 'confirmed')}
                              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Marquer comme confirmé"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateStatus(request.request_id, 'cancelled')}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Annuler"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {request.status === 'confirmed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateStatus(request.request_id, 'processed')}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Marquer comme traité"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} sur {totalPages} ({total} demandes)
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            {/* Header */}
            <div className={`p-6 text-white ${selectedRequest.payment_method === 'moncash' ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 'bg-gradient-to-r from-blue-600 to-blue-700'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-8 h-8" />
                  <div>
                    <h3 className="text-xl font-bold">
                      {selectedRequest.payment_method.toUpperCase()}
                    </h3>
                    <p className="text-white/80 text-sm">{selectedRequest.request_id}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  selectedRequest.status === 'pending' ? 'bg-white/20' :
                  selectedRequest.status === 'confirmed' ? 'bg-blue-200 text-blue-800' :
                  selectedRequest.status === 'processed' ? 'bg-green-200 text-green-800' :
                  'bg-red-200 text-red-800'
                }`}>
                  {getStatusLabel(selectedRequest.status)}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Type</p>
                  <p className="font-semibold">
                    {selectedRequest.payment_type === 'purchase' 
                      ? `Achat (${selectedRequest.quantity} billet${selectedRequest.quantity > 1 ? 's' : ''})` 
                      : 'Don'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Montant</p>
                  <p className="font-bold text-xl text-green-600">${selectedRequest.amount}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Client</p>
                <p className="font-semibold">{selectedRequest.name || 'Non fourni'}</p>
                <p className="text-sm text-gray-600">{selectedRequest.email}</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Numéro à vérifier</p>
                <p className="font-mono font-bold text-lg">{selectedRequest.phone_number}</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Date de la demande</p>
                <p className="font-semibold">{formatDate(selectedRequest.created_at)}</p>
              </div>

              {selectedRequest.affiliate_code && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 mb-1">Code affilié</p>
                  <p className="font-mono font-bold">{selectedRequest.affiliate_code}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                {selectedRequest.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => updateStatus(selectedRequest.request_id, 'confirmed')}
                      disabled={updating}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirmer le paiement
                    </Button>
                    <Button
                      onClick={() => updateStatus(selectedRequest.request_id, 'cancelled')}
                      disabled={updating}
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
                {selectedRequest.status === 'confirmed' && (
                  <Button
                    onClick={() => updateStatus(selectedRequest.request_id, 'processed')}
                    disabled={updating}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Marquer comme traité (billets émis)
                  </Button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <Button
                onClick={() => setSelectedRequest(null)}
                variant="outline"
                className="w-full"
              >
                Fermer
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MobilePaymentsManager;
